import React, { createContext, useContext, useEffect, useState } from 'react';
import { bridgeApi } from '@/lib/bridgeApi';
import { subscribeToPush, getPushStatus } from '@/utils/pushSubscription';

type PatientRole = 'super_admin' | 'patient' | 'visitor' | 'doctor' | 'lab_diagnostic' | 'pharmacy' | 'guest';

interface PatientUser {
  id: string | number;
  user_id?: string;
  legacy_user_id?: number | null;
  role: PatientRole;
  mobile?: string | null;
  phone?: string | null;
  email?: string | null;
  name?: string | null;
  is_verified?: boolean;
  is_active?: boolean;
  guest_used?: boolean;
  session_token?: string;
  created_at?: string;
}

interface PatientAuthContextType {
  user: PatientUser | null;
  patientUser: PatientUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  isGuest: boolean;
  isVisitor: boolean;
  isPatient: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null; user?: PatientUser | null }>;
  signUp: (email: string, password: string, name: string, mobile?: string, role?: PatientRole) => Promise<{ error: Error | null; user?: PatientUser | null }>;
  signOut: () => Promise<void>;
  mobileLogin: (phone: string) => Promise<{ error: Error | null; user?: PatientUser | null }>;
  visitorRegister: (name: string, phone: string, email?: string) => Promise<{ error: Error | null; user?: PatientUser | null }>;
  convertToPatient: (phone: string, password: string, name?: string, email?: string) => Promise<{ error: Error | null }>;
  guestAccess: () => Promise<{ error: Error | null }>;
  convertGuestToPatient: (email: string, password: string, name: string, mobile?: string) => Promise<{ error: Error | null }>;
  refreshPatientUser: () => Promise<void>;
}

const PatientAuthContext = createContext<PatientAuthContextType | undefined>(undefined);

const STORAGE_KEY = 'patakare_patient_auth';

export const PatientAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<PatientUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Load stored session on mount and verify it server-side
  useEffect(() => {
    const verifyStoredSession = async () => {
      try {
        let stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) stored = sessionStorage.getItem(STORAGE_KEY);

        if (!stored) { setLoading(false); return; }

        const userData = JSON.parse(stored) as PatientUser;

        // Guest sessions don't need server verification
        if (userData.role === 'guest') {
          setUser(userData);
          setLoading(false);
          return;
        }

        // Verify session server-side
        try {
          const result = await bridgeApi.call<any>('verify_patient_session', {
            patient_id: userData.id,
            token: userData.session_token,
          });

          if (result?.valid && result?.patient) {
            const fresh = { ...result.patient, session_token: userData.session_token, role: result.patient.role || userData.role };
            setUser(fresh);
            const storage = localStorage.getItem(STORAGE_KEY) ? localStorage : sessionStorage;
            storage.setItem(STORAGE_KEY, JSON.stringify(fresh));
          } else {
            // Session invalid — clear it
            localStorage.removeItem(STORAGE_KEY);
            sessionStorage.removeItem(STORAGE_KEY);
          }
        } catch {
          // Network error — use cached data gracefully (offline-first)
          setUser(userData);
        }
      } catch {
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_KEY);
      } finally {
        setLoading(false);
      }
    };

    verifyStoredSession();
  }, []);

  const saveSession = (userData: PatientUser, persistent = true) => {
    setUser(userData);
    const storage = persistent ? localStorage : sessionStorage;
    storage.setItem(STORAGE_KEY, JSON.stringify(userData));

    // Ask for push permission right here, at register/login — not buried on a page a
    // visitor (mobile-number-only, no email/password) would rarely stumble onto. Only
    // fires for real accounts (this function is never called for guest sessions) and
    // silently no-ops if the platform can't do push at all (e.g. Safari on iOS outside
    // an installed home-screen app has no PushManager) or the user already said no.
    if (userData.role !== 'guest' && userData.id) {
      const { supported, permission } = getPushStatus();
      if (supported && permission !== 'denied') {
        subscribeToPush(userData.id, 'patient').catch(() => {});
      }
    }
  };

  const refreshPatientUser = async () => {
    if (user?.id && user?.role !== 'guest') {
      try {
        const result = await bridgeApi.call<any>('verify_patient_session', { patient_id: user.id, token: user.session_token });
        if (result?.valid && result?.patient) {
          const updatedUser = { ...result.patient, session_token: user.session_token, role: result.patient.role || user.role };
          saveSession(updatedUser);
        }
      } catch (error) {
        console.error('Failed to refresh patient user:', error);
      }
    }
  };

  // Email + password login (for full patients)
  const signIn = async (email: string, password: string) => {
    try {
      // Send as 'identifier' so backend can match by email or phone
      const result = await bridgeApi.post<any>('patient_login', { identifier: email, email, password });
      const patient = result.patient || result.user;
      if (!patient) throw new Error('Invalid response from server');
      
      const userData: PatientUser = {
        ...patient,
        session_token: result.token,
        role: patient.role || 'patient'
      };
      saveSession(userData);
      return { error: null, user: userData };
    } catch (error) {
      return { error: error as Error };
    }
  };

  // Full patient registration with password
  const signUp = async (email: string, password: string, name: string, mobile?: string, role: PatientRole = 'patient') => {
    try {
      const result = await bridgeApi.post<any>('patient_register', {
        name,
        phone: mobile || email,
        email,
        password,
        consent_given: true,
      });
      const patient = result.patient || result.user;
      if (!patient) throw new Error('Invalid response from server');
      
      const userData: PatientUser = {
        ...patient,
        session_token: result.token,
        role: patient.role || 'patient'
      };
      saveSession(userData);
      return { error: null, user: userData };
    } catch (error) {
      return { error: error as Error };
    }
  };

  // Mobile-only login for returning visitors/patients
  const mobileLogin = async (phone: string) => {
    try {
      const result = await bridgeApi.post<any>('patient_mobile_login', { phone });
      const patient = result.patient || result.user;
      if (!patient) throw new Error('Mobile number not registered');
      
      const userData: PatientUser = {
        ...patient,
        session_token: result.token,
        role: patient.role || 'visitor'
      };
      saveSession(userData);
      return { error: null, user: userData };
    } catch (error) {
      return { error: error as Error };
    }
  };

  // Visitor registration (name + mobile + optional email) - saves to MySQL
  const visitorRegister = async (name: string, phone: string, email?: string) => {
    try {
      const result = await bridgeApi.post<any>('visitor_register', { name, phone, email: email || '', consent_given: true });
      const patient = result.patient || result.user;
      if (!patient) throw new Error('Registration failed');
      
      const userData: PatientUser = {
        ...patient,
        session_token: result.token,
        role: patient.role || 'visitor'
      };
      saveSession(userData);
      return { error: null, user: userData };
    } catch (error) {
      return { error: error as Error };
    }
  };

  // Convert visitor to full patient
  const convertToPatient = async (phone: string, password: string, name?: string, email?: string) => {
    try {
      const result = await bridgeApi.post<any>('convert_visitor_to_patient', { phone, password, name, email });
      const patient = result.patient || result.user;
      if (!patient) throw new Error('Conversion failed');
      
      const userData: PatientUser = {
        ...patient,
        session_token: result.token,
        role: 'patient'
      };
      saveSession(userData);
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  // Local guest session (no backend call)
  const guestAccess = async () => {
    try {
      const guestSessionId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const guestUser: PatientUser = {
        id: guestSessionId,
        role: 'guest',
        name: 'Guest User',
        is_active: true,
        session_token: guestSessionId
      };
      setUser(guestUser);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(guestUser));
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const convertGuestToPatient = async (email: string, password: string, name: string, mobile?: string) => {
    if (!user || (user.role !== 'guest' && user.role !== 'visitor')) {
      return { error: new Error('No guest/visitor session to convert') };
    }
    return convertToPatient(mobile || (user.phone ?? ''), password, name, email);
  };

  const signOut = async () => {
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  };

  const isAuthenticated = !!user;
  const isGuest = user?.role === 'guest';
  const isVisitor = user?.role === 'visitor';
  const isPatient = user?.role === 'patient';

  return (
    <PatientAuthContext.Provider value={{
      user,
      patientUser: user,
      loading,
      isAuthenticated,
      isGuest,
      isVisitor,
      isPatient,
      signIn,
      signUp,
      signOut,
      mobileLogin,
      visitorRegister,
      convertToPatient,
      guestAccess,
      convertGuestToPatient,
      refreshPatientUser
    }}>
      {children}
    </PatientAuthContext.Provider>
  );
};

export const usePatientAuth = () => {
  const context = useContext(PatientAuthContext);
  if (context === undefined) {
    throw new Error('usePatientAuth must be used within a PatientAuthProvider');
  }
  return context;
};
